import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, FORM, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

const FIXED_STATUSES = [
	'pickedUp',
	'delivered',
	'returned',
	'confirmedReturn',
	'confirmedReturn',
	'reverseReturned',
	'reverseConfirmReturn',
	'reversePickedUp'
];

const AdminEditIntegratorOrderForm = NiceModal.create((props) => {
	const { id, modalName } = props;
	const { t } = useTranslation();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();
	const [allowedStatuses, setAllowedStatuses] = useState([]);
	const user = useSelector((state) => state.user);
	const { companyId } = user;

	const schema = yup.object().shape({
		forwardDeliveryCompanySettingsId: yup.number().nullable(),
		reverseDeliveryCompanySettingsId: yup.number().nullable(),
		forwardShipmentId: yup.string().nullable(),
		reverseShipmentId: yup.string().nullable(),
		trackingUrl: yup.string().nullable(),
		status: yup.string().nullable()
	});

	const fillForm = async () => {
		const defaultValues = {
			forwardDeliveryCompanySettingsId: null,
			reverseDeliveryCompanySettingsId: null,
			forwardShipmentId: null,
			reverseShipmentId: null,
			trackingUrl: null,
			status: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(ORDER_ACTIONS.GET_INTEGRATOR_ORDER_ADMIN, {
				ID: Number(id)
			});
			const { data, status } = response;

			if (status === 200) {
				return {
					...defaultValues,
					...data
				};
			} else AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError(error);
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isLoading);

	const onSubmit = async (data) => {
		try {
			const response = await axiosPrivate.post(ORDER_ACTIONS.EDIT_INTEGRATOR_ORDER_ADMIN, {
				ID: Number(id),
				...data
			});

			const { status } = response;

			if (status === 200) {
				showSuccess();
				modal.remove();
				dispatch(updateDynamicListsInPage());
			} else AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error.response?.data?.code
			});
		}
	};

	useEffect(() => {
		axiosPrivate
			.post(FORM.GET_ENUM, {
				name: ENUMS.ALL_STATUSES
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setAllowedStatuses(data.filter((status) => !FIXED_STATUSES.includes(status)));
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	}, []);

	return (
		<Modal fullwidth maxWidth="x-large" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">
					{t('adminEdit')} (<strong>{id}</strong>)
				</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="forwardDeliveryCompanySettingsId" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS_ADMIN}
							value={watch('forwardDeliveryCompanySettingsId')}
							setValue={(value) => {
								setValue('forwardDeliveryCompanySettingsId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name={'forwardDeliveryCompanySettingsId'}
							selectionLabelAccessor={['ID', 'Name']}
							columns={[
								{ accessor: 'ID', width: 100 },
								{ accessor: 'Name', width: 1000 }
							]}
							filterObject={{
								userCompanyID: companyId
							}}
						/>
					</FormItem>
					<FormItem label="forwardShipmentId">
						<StringInput name="forwardShipmentId" />
					</FormItem>

					<FormItem label="reverseDeliveryCompanySettingsId" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS_ADMIN}
							value={watch('reverseDeliveryCompanySettingsId')}
							setValue={(value) => {
								setValue('reverseDeliveryCompanySettingsId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name={'reverseDeliveryCompanySettingsId'}
							selectionLabelAccessor={['ID', 'Name']}
							columns={[
								{ accessor: 'ID', width: 100 },
								{ accessor: 'Name', width: 1000 }
							]}
							filterObject={{
								userCompanyID: companyId
							}}
						/>
					</FormItem>
					<FormItem label="reverseShipmentId">
						<StringInput name="reverseShipmentId" />
					</FormItem>

					<FormItem label="status">
						<EnumSelect
							name="status"
							data={allowedStatuses}
							valueRemovable={false}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					<FormItem label="trackingUrl">
						<StringInput name="trackingUrl" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={() => {
						modal.remove();
					}}
				/>

				<Button
					icon={faFloppyDisk}
					label="save"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AdminEditIntegratorOrderForm;
